import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useInbox } from "@/context/InboxContext";
import { useReship } from "@/context/ReshipContext";
import { useRvp } from "@/context/RvpContext";
import { LayoutDashboard, PackageSearch, PackagePlus, Settings, Users, LogOut, Inbox, PanelLeftClose, PanelLeftOpen, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import NotificationBell from "@/components/NotificationBell";
import UserAvatar from "@/components/UserAvatar";

const NAV = [
  { to: "/", label: "Analytics", icon: LayoutDashboard, testid: "nav-analytics", end: true },
  { to: "/inbox", label: "Inbox", icon: Inbox, testid: "nav-inbox", badge: "inbox" },
  { to: "/shipments", label: "NDR Shipments", icon: PackageSearch, testid: "nav-shipments" },
  { to: "/new-shipments", label: "New Shipments", icon: PackagePlus, testid: "nav-new-shipments", badge: "reship" },
  { to: "/reverse-pickup", label: "Reverse Pickup", icon: Undo2, testid: "nav-reverse-pickup", badge: "rvp" },
  { to: "/users", label: "Team", icon: Users, testid: "nav-users", adminOnly: true },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings", adminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const { count: inboxCount } = useInbox();
  const { count: reshipCount } = useReship();
  const { count: rvpCount } = useRvp();
  const navigate = useNavigate();
  const badgeCount = { inbox: inboxCount, reship: reshipCount, rvp: rvpCount };
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar_collapsed") === "1");
  const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem("sidebar_collapsed", c ? "0" : "1"); return !c; });

  return (
    <TooltipProvider delayDuration={200}>
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className={`${collapsed ? "w-20" : "w-64"} shrink-0 bg-[#0B3D3D] text-[#F1EFE0] flex flex-col fixed h-screen transition-[width] duration-200 z-30`}
             data-testid="app-sidebar" data-collapsed={collapsed ? "true" : "false"}>
        <div className={`${collapsed ? "px-3" : "px-6"} py-6 border-b border-white/10`}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-2.5"}`}>
            <img src="/logo.png" alt="GirOrganic" className="h-10 w-10 rounded-full object-cover ring-1 ring-[#C5A253]/50 shrink-0" />
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-[#FBFAF2] text-lg leading-none">GirOrganic</p>
                <p className="text-xs text-[#C5A253] mt-1 tracking-wide font-medium">NDR CONTROL</p>
              </div>
            )}
            {!collapsed && (
              <button onClick={toggleCollapsed} data-testid="sidebar-toggle"
                      className="h-7 w-7 rounded-md flex items-center justify-center text-[#F1EFE0]/70 hover:text-white hover:bg-white/10 transition-colors shrink-0"
                      title="Collapse sidebar">
                <PanelLeftClose className="h-4 w-4" />
              </button>
            )}
          </div>
          {collapsed && (
            <button onClick={toggleCollapsed} data-testid="sidebar-toggle"
                    className="mt-3 mx-auto h-7 w-7 rounded-md flex items-center justify-center text-[#F1EFE0]/70 hover:text-white hover:bg-white/10 transition-colors"
                    title="Expand sidebar">
              <PanelLeftOpen className="h-4 w-4" />
            </button>
          )}
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.filter((n) => !n.adminOnly || isAdmin).map((n) => {
            const link = (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `relative flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border-l-2 ${
                    isActive
                      ? "bg-white/10 text-white border-[#C5A253]"
                      : "text-[#F1EFE0]/65 border-transparent hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <span className="relative shrink-0">
                  <n.icon className="h-4 w-4" />
                  {collapsed && n.badge && badgeCount[n.badge] > 0 && (
                    <span data-testid={`nav-${n.badge}-badge`}
                          className="absolute -top-2 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-[#C24127] text-white text-[9px] font-bold flex items-center justify-center">
                      {badgeCount[n.badge] > 9 ? "9+" : badgeCount[n.badge]}
                    </span>
                  )}
                </span>
                {!collapsed && <span className="flex-1">{n.label}</span>}
                {!collapsed && n.badge && badgeCount[n.badge] > 0 && (
                  <span data-testid={`nav-${n.badge}-badge`}
                        className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#C24127] text-white text-[10px] font-bold flex items-center justify-center">
                    {badgeCount[n.badge] > 9 ? "9+" : badgeCount[n.badge]}
                  </span>
                )}
              </NavLink>
            );
            return collapsed ? (
              <Tooltip key={n.to}>
                <TooltipTrigger asChild>{link}</TooltipTrigger>
                <TooltipContent side="right">{n.label}</TooltipContent>
              </Tooltip>
            ) : link;
          })}
        </nav>
        <div className={`${collapsed ? "px-2" : "px-4"} py-4 border-t border-white/10`}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => navigate("/profile")} data-testid="sidebar-profile"
                          className="rounded-full p-1 hover:bg-white/5 transition-colors">
                    <UserAvatar name={user?.name} avatar={user?.avatar} size={36} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">{user?.name}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={logout} data-testid="logout-button"
                          className="h-9 w-9 rounded-lg flex items-center justify-center border border-white/20 text-[#F1EFE0] hover:bg-white/10 hover:text-white transition-colors">
                    <LogOut className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">Sign out</TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <>
              <button onClick={() => navigate("/profile")} data-testid="sidebar-profile"
                      className="w-full flex items-center gap-2 mb-3 rounded-lg p-1.5 hover:bg-white/5 transition-colors text-left">
                <UserAvatar name={user?.name} avatar={user?.avatar} size={36} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#FBFAF2] truncate" data-testid="sidebar-user-name">{user?.name}</p>
                  <p className="text-xs text-[#C5A253] capitalize">{user?.role}</p>
                </div>
              </button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2 bg-transparent border-white/20 text-[#F1EFE0] hover:bg-white/10 hover:text-white"
                data-testid="logout-button"
                onClick={logout}
              >
                <LogOut className="h-4 w-4" /> Sign out
              </Button>
            </>
          )}
        </div>
      </aside>
      <main className={`flex-1 ${collapsed ? "ml-20" : "ml-64"} min-w-0 min-h-screen flex flex-col transition-[margin] duration-200`}>
        <header className="h-16 shrink-0 border-b border-border bg-white flex items-center justify-end gap-2 px-8 sticky top-0 z-20">
          <NotificationBell />
          <button data-testid="header-profile" onClick={() => navigate("/profile")}
                  className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-[#F0F2F0] transition-colors">
            <UserAvatar name={user?.name} avatar={user?.avatar} size={32} />
            <span className="text-sm font-medium text-[#111411] max-w-[120px] truncate hidden sm:block">{user?.name}</span>
          </button>
        </header>
        <div className="p-8 flex-1">{children}</div>
      </main>
    </div>
    </TooltipProvider>
  );
}
